
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { doGet } from "@/store/common/module/apis";
import common from "@/store/common/module/common";
import corpService from "@/store/common/module/CorpService";
import { EventBus } from "./eventBus.js";
import { Tariff } from "./TariffInterfaces";

interface Grid {
  page: number;
  rows: Tariff[];
  records: number | null;
  total: number | null;
}

interface Filter {
  client_id: string | null;
  vehicleType_id: string | null;
}


@Component({
  name: "TariffGrid",
})
export default class TariffGrid extends Vue {
  @Prop({ default: false }) readonly originManager!: boolean;
  @Prop({ default: null }) readonly client_id!: string | null;

  url = "/api/tariff/grid?";
  grid: Grid = { page: 1, rows: [], records: null, total: null };
  private inRow = 20;
  private filter: Filter = {
    client_id: null,
    vehicleType_id: null,
  };

  get loading(): boolean {
    return common.loading;
  }

  private prepareUrl(initUrl: string): string {
    let url = initUrl;
    const client_id = this.filter.client_id ? this.filter.client_id : this.client_id;

    if (client_id) {
      url = `${url}&client_id=${client_id}`;
    }
    if (this.filter.vehicleType_id) {
      url = `${url}&vehicleType_id=${this.filter.vehicleType_id}`;
    }
    return url;
  }

  async fetch() {
    try {
      await this.fetchPage(this.grid.page);
    } catch (error) {
      if (error instanceof Error) {
        common.setError(error.message);
      } else {
        common.setError('An error occurred.');
      }
    } finally {
      common.setLoading(false);
    }

  }
  @Watch("client_id", { immediate: true })
  onClientIdChange(newId: string, oldId: string) {
    if (newId !== oldId) {
      this.filter.client_id = newId;
      this.fetch();
    }
  }
  async fetchPage(page: number) {
    this.grid.page = page;
    common.setLoading(true);
    const url = `${this.prepareUrl(this.url)}&rows=${this.inRow}&page=${this.grid.page}`;

    try {
      const response = await doGet(url);
      if (response) {
        this.grid = { ...response };
      }
    } catch (error) {
      if (error instanceof Error) {
        common.setError(error.message);
      } else {
        common.setError('An error occurred.');
      }
    } finally {
      common.setLoading(false);
    }
  }

  setClientName(_id: string): string {
    if (!_id) return "";
    const corporates = corpService.clients;
    const corp = corporates.find(c => c._id === _id);
    return corp ? corp.name : "";
  }

  setVehicleCategoryName(_id: string): string {
    if (!_id) return "";
    const vehicleCategories = corpService.vehicleCategories;
    const vehicleCategorie = vehicleCategories.find(v => v._id === _id);
    return vehicleCategorie ? vehicleCategorie.name : "";
  }

  selectTariff(tariff: Tariff) {
    this.$emit("change", tariff);
  }

  handleCreate() {
    if (this.originManager) {
      this.$emit("create", null);
    } else {
      this.$router.push("/tariff/create");
    }
  }

  handleEdit(_id: string | null) {
    if (this.originManager) {
      this.$emit("edit", _id);
    } else {
      this.$router.push(`/tariff/edit/${_id}`);
    }
  }

  created() {
    EventBus.$on("tariffSaved", this.fetch);
  }

  beforeDestroy() {
    EventBus.$off("tariffSaved", this.fetch); // Important to avoid memory leaks
  }

  async handleDelete(_id: string | null) {
    if (!_id) return;
    common.setLoading(true);
    try {
      await doGet(`/api/tariff/${_id}/delete`);
      await this.fetch();
      common.showMessage("Deleted Successfully");
    } catch (error) {
      if (error instanceof Error) {
        common.setError(error.message);
      } else {
        common.setError('An error occurred.');
      }
    } finally {
      common.setLoading(false);
    }
  }

  async mounted() {
    try {
      await corpService.populateClients();
      await corpService.populateVehicleCategories();
      await this.fetch();
    } catch (error) {
      if (error instanceof Error) {
        common.setError(error.message);
      } else {
        common.setError('An error occurred.');
      }
    }
  }

}
